import KybEvidencePackRepository from "@/core/kyb/repositories/KybEvidencePackRepository";
import {
  KybEvidencePackExport,
  KybEvidencePackFile,
  KybEvidencePackFormat,
  KybSnapshotHistory,
  KybSnapshotSummary,
} from "@/core/kyb/entities/KybEvidencePack";
import { RepositoryResult, RepositoryResultFactory } from "@/core/shared/types/RepositoryResult";
import Exception from "@/core/exceptions/Exception";
import DefaultExceptionTypes from "@/core/exceptions/DefaultExceptionTypes";
import ViteEnv from "@/details/env/vite/ViteEnv";
import EnvKeys from "@/core/env/EnvKeys";
import { useAuthStore } from "@/core/stores/authStore";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { executeKybRequest, toKybException } from "../../http/services/kybRequest/kybRequest";
import kybEvidencePackEndpoints from "../../api/kybEvidencePackEndpoints/kybEvidencePackEndpoints";

type SnapshotDto = {
  snapshotId: string;
  sequenceNo: number;
  trigger: string;
  triggerReference?: string | null;
  capturedAt: string;
  capturedBy?: string | null;
  onDemand: boolean;
  contentHash: string;
  entryHash: string;
  previousHash?: string | null;
  intact: boolean;
};

type HistoryDto = {
  intact: boolean;
  checked: number;
  firstAnomaly?: { sequenceNo: number; reason: string } | null;
  snapshots: SnapshotDto[];
};

type ExportDto = {
  exportId: string;
  format: string;
  outcome: "EXPORTED" | "REFUSED";
  packHash?: string | null;
  onDemandSnapshot: boolean;
  refusalReason?: string | null;
  actor: string;
  exportedAt: string;
};

type PackDto = { packHash?: string | null } & Record<string, unknown>;

/** Le back rend `null` pour une valeur absente : l'écran travaille avec des champs optionnels. */
const orUndefined = <T>(value: T | null | undefined): T | undefined => value ?? undefined;

const mapSnapshot = (dto: SnapshotDto): KybSnapshotSummary => ({
  snapshotId: dto.snapshotId,
  sequenceNo: dto.sequenceNo,
  trigger: dto.trigger,
  triggerReference: orUndefined(dto.triggerReference),
  capturedAt: dto.capturedAt,
  capturedBy: orUndefined(dto.capturedBy),
  onDemand: dto.onDemand,
  contentHash: dto.contentHash,
  entryHash: dto.entryHash,
  previousHash: orUndefined(dto.previousHash),
  intact: dto.intact,
});

function exceptionType(status: number): DefaultExceptionTypes {
  switch (status) {
    case 400:
      return DefaultExceptionTypes.BAD_REQUEST;
    case 401:
      return DefaultExceptionTypes.UNAUTHORIZED;
    case 403:
      return DefaultExceptionTypes.FORBIDDEN;
    case 404:
      return DefaultExceptionTypes.NOT_FOUND;
    case 409:
      return DefaultExceptionTypes.CONFLICT;
    default:
      return DefaultExceptionTypes.UNKNOWN_ERROR;
  }
}

/** Refus de l'export (droit manquant, chaîne compromise) : le message du back est ce qui l'explique. */
async function refusalOf(response: Response): Promise<Exception<unknown, DefaultExceptionTypes>> {
  const body = (await response.json().catch(() => null)) as { message?: string } | null;
  return { type: exceptionType(response.status), message: body?.message || undefined };
}

function fileNameOf(disposition: string | null, fallback: string): string {
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1]?.trim() || fallback;
}

export default class KybEvidencePackRepositoryBackend implements KybEvidencePackRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async getSnapshots(caseId: string): Promise<RepositoryResult<KybSnapshotHistory>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<HistoryDto>>({
          url: kybEvidencePackEndpoints.snapshots(caseId),
          withAuth: true,
        }),
      (dto) => ({
        intact: dto.intact,
        checked: dto.checked,
        firstAnomaly: dto.firstAnomaly ?? undefined,
        snapshots: dto.snapshots.map(mapSnapshot),
      }),
    );
  }

  async getExports(caseId: string): Promise<RepositoryResult<KybEvidencePackExport[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<ExportDto[]>>({
          url: kybEvidencePackEndpoints.exports(caseId),
          withAuth: true,
        }),
      (dtos) =>
        dtos.map((dto) => ({
          exportId: dto.exportId,
          format: dto.format,
          outcome: dto.outcome,
          packHash: orUndefined(dto.packHash),
          onDemandSnapshot: dto.onDemandSnapshot,
          refusalReason: orUndefined(dto.refusalReason),
          actor: dto.actor,
          exportedAt: dto.exportedAt,
        })),
    );
  }

  async exportPack(caseId: string, format: KybEvidencePackFormat): Promise<RepositoryResult<KybEvidencePackFile>> {
    try {
      const base = String(new ViteEnv().get(EnvKeys.BACKEND_BASE_URL) ?? "").replace(/\/$/, "");
      const path = kybEvidencePackEndpoints.exportPack(caseId, format);
      const token = useAuthStore.getState().user?.token;
      // Le HttpClient ne rend pas de binaire : même patron que l'export du graphe, un fetch avec le jeton.
      const response = await fetch(base ? `${base}/${path.replace(/^\//, "")}` : path, {
        method: "POST",
        headers: token ? { "X-App-Token": `Bearer ${token}` } : undefined,
      });
      if (!response.ok) {
        return RepositoryResultFactory.error(await refusalOf(response));
      }
      if (format === "PDF") {
        return RepositoryResultFactory.success({
          fileName: fileNameOf(response.headers.get("Content-Disposition"), `evidence-pack-${caseId}.pdf`),
          mimeType: "application/pdf",
          blob: await response.blob(),
          packHash: orUndefined(response.headers.get("X-Pack-Hash")),
        });
      }
      const body = (await response.json()) as ApiResponse<PackDto>;
      return RepositoryResultFactory.success({
        fileName: `evidence-pack-${caseId}.json`,
        mimeType: "application/json",
        blob: new Blob([JSON.stringify(body.data, null, 2)], { type: "application/json" }),
        packHash: orUndefined(body.data?.packHash),
      });
    } catch (error) {
      return RepositoryResultFactory.error(toKybException(error as ApiResponse<unknown>));
    }
  }
}
